// Composer Studio — 작성 캔버스 위젯 경계 드래그(폭 비율 span 조정)의 순수 재배분 로직.
//
// 캔버스에서 같은 줄에 놓인 **인접한 두 위젯 사이 경계**를 끌면 두 위젯의 span(0~1 비율)을 조정한다.
// DOM 측정과 드래그 UX는 WidgetEdgeResizeOverlay가 담당하고, 여기서는 "시작 비율 쌍 + 행 폭 +
// 포인터 델타 → 새 비율 쌍"의 순수 계산만 분리해 단위 테스트한다(표 경계의 tableResize 대응물).
//
// 정책:
//   - **조작하는 쌍의 합을 보존한다.** 좌측이 넓어진 만큼 우측이 좁아진다 — 같은 행의 다른 위젯도,
//     줄 끝 빈 트랙(filler)도 건드리지 않는다. 그래서 "합<1인 행은 남는 폭을 빈칸으로 둔다"는
//     데이터 규칙(computeGroupRatioRowLayout)이 드래그로도 깨지지 않는다. 드래그는 합을 1로
//     강제하는 조작이 **아니다**.
//   - 미지정(상속) 위젯은 드래그 시작 시 **실측 px ÷ 행 트랙 폭**으로 구체 비율 전환(표 'auto' 열의
//     실측→구체 전환, NodeEdgeResizeOverlay의 fill/hug→실측 px 전환과 동형).
//   - 하한 MIN_SPAN_RATIO — 0이나 음수로 떨어져 위젯이 화면에서 사라지는 것을 막는다.
//   - 결과는 소수 4자리로 반올림(JSON에 0.30000000000000004 같은 부동소수 잡음을 남기지 않는다).
//     반올림 탓에 쌍의 합은 최대 1e-4까지 달라질 수 있다(화면상 무의미한 오차).

/** 위젯 폭 비율 하한 — 이보다 좁아지면 위젯이 사실상 사라진다. */
export const MIN_SPAN_RATIO = 0.05;

export interface SpanPairResizeInput {
  /** 좌측 위젯의 현재 해소 비율(undefined=미지정 → 실측으로 구체 전환). */
  leftRatio?: number;
  /** 우측 위젯의 현재 해소 비율(undefined=미지정). */
  rightRatio?: number;
  /** 좌측 위젯 칸의 실측 폭(px, 미스케일). 미지정 위젯의 구체 전환 기준. */
  leftMeasuredPx: number;
  /** 우측 위젯 칸의 실측 폭(px, 미스케일). */
  rightMeasuredPx: number;
  /** 비율 1.0에 해당하는 행 폭(px) — 행 grid 트랙 폭의 합(칸 사이 gap 제외). */
  rowRatioWidthPx: number;
  /** 드래그 시작점 대비 포인터 가로 이동(px, 미스케일). 오른쪽=양수(좌측 위젯이 넓어진다). */
  deltaPx: number;
  /** 비율 하한(기본 MIN_SPAN_RATIO). */
  minRatio?: number;
}

export interface SpanPairResizeResult {
  left: number;
  right: number;
}

/** 소수 4자리 반올림(0.0001 = 0.01% 해상도 — 저작 UI의 % 입력보다 촘촘하다). */
function roundRatio(v: number): number {
  return Math.round(v * 10000) / 10000;
}

/**
 * 현재 비율을 구체값으로 해소 — 지정값이 있으면 그 값(1 상한), 없으면 실측 px ÷ 행 폭.
 * 행 폭이 0이면(측정 실패) 0을 돌려 호출부의 무변경 가드에 걸리게 한다.
 */
function toConcreteRatio(
  ratio: number | undefined,
  measuredPx: number,
  rowRatioWidthPx: number,
): number {
  if (ratio !== undefined && Number.isFinite(ratio) && ratio > 0) return Math.min(1, ratio);
  if (!(rowRatioWidthPx > 0) || !Number.isFinite(measuredPx) || measuredPx <= 0) return 0;
  return Math.min(1, measuredPx / rowRatioWidthPx);
}

/**
 * 인접 두 위젯의 경계를 deltaPx만큼 끌었을 때의 새 비율 쌍을 만든다(합 보존).
 *
 * 미지정 위젯은 실측으로 구체 전환하므로, 드래그 한 번으로 두 위젯 모두 명시 비율을 갖게 된다
 * (= 화면 그대로의 값에서 출발해 조정된다 — WYSIWYG).
 * 측정 실패(행 폭 0)나 쌍의 합이 0이면 계산 없이 시작값을 그대로 돌린다(무변경).
 */
export function resizeSpanPair(input: SpanPairResizeInput): SpanPairResizeResult {
  const min = input.minRatio ?? MIN_SPAN_RATIO;
  const left0 = toConcreteRatio(input.leftRatio, input.leftMeasuredPx, input.rowRatioWidthPx);
  const right0 = toConcreteRatio(input.rightRatio, input.rightMeasuredPx, input.rowRatioWidthPx);
  const sum = left0 + right0;
  if (!(input.rowRatioWidthPx > 0) || !(sum > 0) || !Number.isFinite(input.deltaPx)) {
    return { left: roundRatio(left0), right: roundRatio(right0) };
  }
  // 쌍이 하한 두 몫도 못 채울 만큼 좁으면(비정상 데이터) 반씩 나눠 하한 클램프가 뒤집히지 않게 한다.
  const lo = Math.min(min, sum / 2);
  const hi = Math.max(sum - min, sum / 2);
  const next = Math.min(hi, Math.max(lo, left0 + input.deltaPx / input.rowRatioWidthPx));
  return { left: roundRatio(next), right: roundRatio(sum - next) };
}
